import { useState, useEffect } from 'react';
import { useObjectsTab, ObjectModel } from './hooks/HObjects';
import useWatch from './hooks/HWatch';
import { useRequests } from './hooks/HRequests';
import { useMessages } from './hooks/HMessages';
import { User } from './interfaces/IUsers';
import { getAccessRequestMessage } from '../utils/helpers';
import DashboardTopCards from '../misc/cards';
import DealsSection from '../misc/deals';
import SettingsTab from './SettingsTab';

const OBJECT_ASSET_CLASS_OPTIONS = ['RE', 'Funds', 'Collections', 'MA'] as const;

interface DashboardTabProps {
    onTabClick?: (tab: string, object?: any) => void;
    user?: User | null;
    filter?: string;
}

export default function DashboardTab({ onTabClick, user, filter = 'RE' }: DashboardTabProps) {
		const {
			allObjects,
			loading,
			selectedRows,
			loadUserObjects,
		getObjectsByUserId,
		createQuickObject,
		updateObject,
		deleteObject,
		toggleRowSelection,
		selectAllRows,
		fetchAllTypes,
		fetchAllStatus,
		fetchAllZonings,
	} = useObjectsTab();

	const [searchInput, setSearchInput] = useState('');
	const [filterInput, setFilterInput] = useState(filter);
	const [displayedObjects, setDisplayedObjects] = useState<ObjectModel[]>([]);
	const [accessibleObjectIds, setAccessibleObjectIds] = useState<Set<string>>(new Set());
	const [snackbar, setSnackbar] = useState<{
		open: boolean;
		message: string;
		severity: 'success' | 'error';
	}>({ open: false, message: '', severity: 'success' });

	// Watchlist support
	const userId = user?.id ?? user?.user_id ?? null;
	const { watchList, loadWatchList } = useWatch(userId);

	// Requests counts
	const { loadAllRequests } = useRequests();
	const [reqOwnerCount, setReqOwnerCount] = useState<number>(0);
	const [reqUserCount, setReqUserCount] = useState<number>(0);

	// Messages counts
	const { loadAllMessages } = useMessages();
	const [msgOwnerCount, setMsgOwnerCount] = useState<number>(0);
	const [msgUserCount, setMsgUserCount] = useState<number>(0);


	useEffect(() => {
		if (!userId) {
			setReqOwnerCount(0);
			setReqUserCount(0);
			return;
		}
		const fetchCounts = async () => {
			const all = await loadAllRequests();
			const ownerList = Array.isArray(all) ? all.filter((r: any) => String(r.object_owner_id) === String(userId)) : [];
			const userList = Array.isArray(all) ? all.filter((r: any) => String(r.user) === String(userId)) : [];
			setReqOwnerCount(ownerList.length);
			setReqUserCount(userList.length);
		};
		void fetchCounts();
	}, [userId, loadAllRequests]);

	useEffect(() => {
		if (!userId) {
			setMsgOwnerCount(0);
			setMsgUserCount(0);
			return;
		}
		const fetchMsgCounts = async () => {
			const all = await loadAllMessages();
			const ownerList = Array.isArray(all) ? all.filter((m: any) => String(m.owner_id) === String(userId)) : [];
			const userList = Array.isArray(all) ? all.filter((m: any) => String(m.user_id ?? m.user) === String(userId)) : [];
			setMsgOwnerCount(ownerList.length);
			setMsgUserCount(userList.length);
		};
		void fetchMsgCounts();
	}, [userId, loadAllMessages]);



	const [typeFilter, setTypeFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [zoningFilter, setZoningFilter] = useState('');
	const [typeOptions, setTypeOptions] = useState<string[]>([]);
	const [statusOptions, setStatusOptions] = useState<string[]>([]);
	const [zoningOptions, setZoningOptions] = useState<string[]>([]);

	const [formDialogOpen, setFormDialogOpen] = useState(false);
	const [formData, setFormData] = useState<Partial<ObjectModel>>({
		name: '',
		assetclass: OBJECT_ASSET_CLASS_OPTIONS[0],
		company: '',
		description: '',
	});

	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<ObjectModel | null>(null);
	const [accessRequestDialogOpen, setAccessRequestDialogOpen] = useState(false);
	const [accessRequestObjectId, setAccessRequestObjectId] = useState<string | number | null>(null);

	useEffect(() => {
		void loadUserObjects(user, filterInput);
	}, [loadUserObjects, filterInput, user]);

	useEffect(() => {
		const loadAccessibleObjects = async () => {
			const objects = await getObjectsByUserId(user?.id ?? user?.user_id, filterInput);
			setAccessibleObjectIds(new Set(objects.map((item) => String(item.id))));
		};

		void loadAccessibleObjects();
	    }, [getObjectsByUserId, filterInput, user?.id, user?.user_id]);

	// Load watch list when user changes so WatchList card shows correct count
	useEffect(() => {
		if (!userId) return;
		void loadWatchList();
	}, [userId, loadWatchList]);

	useEffect(() => {
		const query = searchInput.trim().toLowerCase();
		let next = query
			? allObjects.filter((item) => (item.name ?? '').toLowerCase().includes(query))
			: allObjects;
		if (typeFilter) next = next.filter((item) => String(item.type_ ?? '').toLowerCase() === typeFilter.toLowerCase());
		if (statusFilter) next = next.filter((item) => String(item.status ?? '').toLowerCase() === statusFilter.toLowerCase());
		if (zoningFilter) next = next.filter((item) => String(item.zoning ?? '').toLowerCase() === zoningFilter.toLowerCase());
		setDisplayedObjects(next);
	}, [allObjects, searchInput, typeFilter, statusFilter, zoningFilter]);

	useEffect(() => {
		setFilterInput(filter || 'RE');
	}, [filter]);

	useEffect(() => {
		fetchAllTypes().then((rows) => setTypeOptions(rows.map((r) => r.description ?? '').filter(Boolean)));
		fetchAllStatus().then((rows) => setStatusOptions(rows.map((r) => r.description ?? '').filter(Boolean)));
		fetchAllZonings().then((rows) => setZoningOptions(rows.map((r) => r.description ?? '').filter(Boolean)));
	}, [fetchAllTypes, fetchAllStatus, fetchAllZonings]);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInput(event.target.value);
	};

	const handleAddObject = () => {
		setFormData({ name: '', assetclass: OBJECT_ASSET_CLASS_OPTIONS[0], company: '', description: '' });
		setFormDialogOpen(true);
	};

	const handleEditObject = async (object: ObjectModel, updatedData: Partial<ObjectModel>) => {
		try {
			const success = await updateObject(object.id, updatedData);
			if (success) {
				setSnackbar({
					open: true,
					message: 'Object updated successfully.',
					severity: 'success',
				});
				return;
			}

			setSnackbar({
				open: true,
				message: 'Failed to update object.',
				severity: 'error',
			});
		} catch (err: any) {
			setSnackbar({
				open: true,
				message: err.message || 'Failed to save object.',
				severity: 'error',
			});
		}
	};

	const handleFormSubmit = async () => {
		if (!formData.name || !formData.name.trim()) {
			setSnackbar({
				open: true,
				message: 'Object name is required.',
				severity: 'error',
			});
			return;
		}

		try {
			const success = await createQuickObject(formData);
			if (success) {
				setSnackbar({
					open: true,
					message: 'Object created successfully.',
					severity: 'success',
				});
			}

			if (success) {
				setFormDialogOpen(false);
				setFormData({ name: '', assetclass: OBJECT_ASSET_CLASS_OPTIONS[0], company: '', description: '' });
			}
		} catch (err: any) {
			setSnackbar({
				open: true,
				message: err.message || 'Failed to save object.',
				severity: 'error',
			});
		}
	};

	const handleDeleteClick = (object: ObjectModel) => {
		setDeleteTarget(object);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!deleteTarget) return;

		try {
			const success = await deleteObject(deleteTarget);
			if (success) {
				setSnackbar({
					open: true,
					message: 'Object deleted successfully.',
					severity: 'success',
				});
			} else {
				setSnackbar({
					open: true,
					message: 'Failed to delete object.',
					severity: 'error',
				});
			}
		} catch (err: any) {
			setSnackbar({
				open: true,
				message: err.message || 'Failed to delete object.',
				severity: 'error',
			});
		} finally {
			setDeleteDialogOpen(false);
			setDeleteTarget(null);
		}
	};

	const handleOpenObjectDetails = (object: ObjectModel) => {
		const objectId = object?.id;
		if (objectId === null || objectId === undefined) return;

		if (!accessibleObjectIds.has(String(objectId))) {
			setAccessRequestObjectId(objectId);
			setAccessRequestDialogOpen(true);
			return;
		}

		const hash = `#objectdetails/${encodeURIComponent(String(objectId))}`;
		window.location.hash = hash;
		onTabClick?.('objectdetails', { ...object, user });
	};

	const handleAccessRequestSubmit = () => {
		const message = getAccessRequestMessage(user, 'Access to object requested');
		setSnackbar({
			open: true,
			message,
			severity: 'success',
		});
		setAccessRequestDialogOpen(false);
		setAccessRequestObjectId(null);
	};

	return (
		<div className='standard-div'>
			<div className='standard-div-flex'>
			
					<DashboardTopCards
						onTabClick={onTabClick}
						watchList={watchList}
						reqOwnerCount={reqOwnerCount}
						reqUserCount={reqUserCount}
						msgOwnerCount={msgOwnerCount}
						msgUserCount={msgUserCount}
					/>

			{user?.role_id=== 1 ? (
				<SettingsTab />
			) : (
				<DealsSection
				displayedObjects={displayedObjects}
				loading={loading}
				typeFilter={typeFilter}
				setTypeFilter={setTypeFilter}
				typeOptions={typeOptions}
				statusFilter={statusFilter}
				setStatusFilter={setStatusFilter}
				statusOptions={statusOptions}
				zoningFilter={zoningFilter}
				setZoningFilter={setZoningFilter}
				zoningOptions={zoningOptions}
				searchInput={searchInput}
				handleSearchChange={handleSearchChange}
				handleAddObject={handleAddObject}
				formDialogOpen={formDialogOpen}
				setFormDialogOpen={setFormDialogOpen}
				formData={formData}
				setFormData={setFormData}
				handleFormSubmit={handleFormSubmit}
				accessRequestDialogOpen={accessRequestDialogOpen}
				accessRequestObjectId={accessRequestObjectId}
				setAccessRequestDialogOpen={setAccessRequestDialogOpen}
				setAccessRequestObjectId={setAccessRequestObjectId}
				handleAccessRequestSubmit={handleAccessRequestSubmit}
				deleteDialogOpen={deleteDialogOpen}
				setDeleteDialogOpen={setDeleteDialogOpen}
				deleteTarget={deleteTarget}
				handleDeleteConfirm={handleDeleteConfirm}
				selectedRows={selectedRows}
				toggleRowSelection={toggleRowSelection}
				selectAllRows={selectAllRows}
				handleEditObject={handleEditObject}
				handleDeleteClick={handleDeleteClick}
				user={user}
				handleOpenObjectDetails={handleOpenObjectDetails}
				snackbar={snackbar}
				setSnackbar={setSnackbar}
				assetClassOptions={OBJECT_ASSET_CLASS_OPTIONS}
			/>
			)
		}
			</div>
		</div>
	);
}
